import { BlockDomain } from '@strength/domain';
import { DateTime } from 'luxon';
import { BlockPreview, WorkoutWithCycleAndSession } from '../types';

export type CalendarDay = {
  date: string;
  isInBlock: boolean;
};

export type CalendarWeek = {
  days: CalendarDay[]; // 7 days, Sun (index 0) through Sat (index 6)
};

export type MonthBoundary = {
  weekIndex: number;
  isoDate: string;
};

/**
 * Luxon `weekday`: Mon=1 … Sat=6, Sun=7. Calendar grid weeks start on Sunday.
 */
function sundayOnOrBefore(dt: DateTime): DateTime {
  return dt.minus({ days: dt.weekday % 7 });
}

/**
 * Saturday at the end of the Sun–Sat week that contains `dt`.
 */
function saturdayOnOrAfter(dt: DateTime): DateTime {
  return dt.plus({ days: 6 - (dt.weekday % 7) });
}

export function groupWorkoutsByDate(
  workouts: WorkoutWithCycleAndSession[],
): Map<string, WorkoutWithCycleAndSession[]> {
  const grouped = new Map<string, WorkoutWithCycleAndSession[]>();
  for (const workout of workouts) {
    if (workout.assignedDate === null) continue;
    const bucket = grouped.get(workout.assignedDate);
    if (bucket) {
      bucket.push(workout);
    } else {
      grouped.set(workout.assignedDate, [workout]);
    }
  }
  return grouped;
}

/**
 * Map a calendar day to the cycle that contains it. A block is modeled as
 * cycles → sessions (not calendar dates), while the All view is date-first, so
 * this is the bridge: it returns the first cycle whose `[startDate, endDate]`
 * range (both inclusive) contains `date`, or `null` for padding days that fall
 * in a gap between cycles or outside the block entirely.
 *
 * Cycle and calendar dates are date-only ISO strings (`YYYY-MM-DD`), which
 * compare correctly lexicographically.
 */
export function getCycleForDate(
  date: string,
  sortedCycles: BlockPreview['sortedCycles'],
): BlockPreview['sortedCycles'][number] | null {
  return (
    sortedCycles.find(
      (cycle) => cycle.startDate <= date && date <= cycle.endDate,
    ) ?? null
  );
}

export type CycleSessionForInsert = {
  workoutId: string;
  sessionSortOrder: number;
  assignedDate: string | null;
};

export type DateOrderedInsertPosition = {
  /** The sort order the new session should occupy within its cycle. */
  sessionSortOrder: number;
  /** Workout ids of existing siblings whose sort order must be bumped by 1. */
  bumpedWorkoutIds: string[];
};

/**
 * Compute where a new workout for `clickedDate` slots into a cycle's existing
 * sessions, mirroring the cycle view's insert-and-bump (`onAddSession`).
 *
 * The slot is `(# sessions dated on/before the clicked day) + 1`. The count is
 * inclusive of same-day sessions, so a new workout sorts *after* any existing
 * workout already dated to the clicked day. Every sibling at or after that slot
 * is bumped by one to make room. This is placement at creation time only —
 * re-dating a workout later does not resequence (see the parent ticket).
 *
 * Undated siblings don't participate in the date count but are still bumped if
 * their current sort order is at or after the computed slot.
 */
export function getDateOrderedInsertPosition(
  cycleSessions: CycleSessionForInsert[],
  clickedDate: string,
): DateOrderedInsertPosition {
  const datedOnOrBefore = cycleSessions.filter(
    (s) => s.assignedDate !== null && s.assignedDate <= clickedDate,
  ).length;
  const sessionSortOrder = datedOnOrBefore + 1;

  const bumpedWorkoutIds = cycleSessions
    .filter((s) => s.sessionSortOrder >= sessionSortOrder)
    .map((s) => s.workoutId);

  return { sessionSortOrder, bumpedWorkoutIds };
}

/**
 * Count the sessions in each cycle, keyed by `cycle.id`. The caller passes the
 * workouts for the active layer (base block vs selected athlete), so the counts
 * are automatically scoped to that layer. Used to gate the "+ Add Workout" CTA
 * once a cycle hits its session cap.
 */
export function getSessionCountByCycle(
  workouts: Pick<WorkoutWithCycleAndSession, 'cycle'>[],
): Map<number, number> {
  const counts = new Map<number, number>();
  for (const { cycle } of workouts) {
    counts.set(cycle.id, (counts.get(cycle.id) ?? 0) + 1);
  }
  return counts;
}

/**
 * Whether a cycle has reached the per-cycle session cap
 * (`BlockDomain.DEFAULT_CYCLE_LENGTH_IN_DAYS`). Because multiple day cells map
 * to one cycle, a capped cycle reports at-cap for every one of its cells, so
 * the CTA is hidden across the whole cycle.
 */
export function isCycleAtCap(
  sessionCountByCycle: Map<number, number>,
  cycleId: number,
): boolean {
  return (
    (sessionCountByCycle.get(cycleId) ?? 0) >=
    BlockDomain.DEFAULT_CYCLE_LENGTH_IN_DAYS
  );
}

export function getMonthYearLabel(isoDate: string, locale: string): string {
  return DateTime.fromISO(isoDate, { zone: 'utc' })
    .setLocale(locale)
    .toFormat('LLLL yyyy');
}

export function isFirstOfMonth(isoDate: string): boolean {
  return DateTime.fromISO(isoDate, { zone: 'utc' }).day === 1;
}

export function getMonthBoundaries(weeks: CalendarWeek[]): MonthBoundary[] {
  const boundaries: MonthBoundary[] = [];
  weeks.forEach((week, weekIndex) => {
    const firstOfMonth = week.days.find((day) => isFirstOfMonth(day.date));
    if (firstOfMonth) {
      boundaries.push({ weekIndex, isoDate: firstOfMonth.date });
    }
  });
  return boundaries;
}

export function buildCalendarWeeks(block: BlockPreview): CalendarWeek[] {
  const blockStart = DateTime.fromISO(block.startDate, { zone: 'utc' });
  const blockEnd = DateTime.fromISO(block.endDate, { zone: 'utc' });

  const gridStart = sundayOnOrBefore(blockStart);
  const gridEnd = saturdayOnOrAfter(blockEnd);

  const weeks: CalendarWeek[] = [];
  let weekStart = gridStart;

  while (weekStart <= gridEnd) {
    const days: CalendarDay[] = Array.from({ length: 7 }, (_, i) => {
      const day = weekStart.plus({ days: i });
      return {
        date: day.toISODate()!,
        isInBlock: day >= blockStart && day <= blockEnd,
      };
    });
    weeks.push({ days });
    weekStart = weekStart.plus({ weeks: 1 });
  }

  return weeks;
}
